import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import AkBreadcrumbs from '@atlaskit/breadcrumbs/breadcrumbs';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs/breadcrumbs-item';

import { addQueryParams } from '@confluence/route-manager-utils/entry-points/url.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';

import { useSearchBreadcrumbs } from './SearchBreadcrumbsProvider.tsx';
import type { breadcrumbAncestor } from './SearchBreadcrumbsProvider.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchBreadcrumbsWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'nav > ol': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'li:not(:last-child)::after': {
			padding: `0px ${token('space.050')}`,
			width: '4px',
			color: token('color.text.subtle'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		span: {
			color: token('color.text.subtle'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'li a': {
			font: token('font.body.small'),
		},
	},
});

type SearchBreadcrumbProps = {
	index: number;
	contentId: string;
	spaceKey: string;
	ancestors?: breadcrumbAncestor[];
};

export const SearchBreadcrumb: FC<SearchBreadcrumbProps> = ({
	index,
	contentId,
	spaceKey,
	ancestors,
}) => {
	const [{ searchSessionId }] = useSearchSessionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	let pageAncestors;
	const { getAncestors } = useSearchBreadcrumbs();
	if (ancestors) {
		pageAncestors = ancestors;
	} else {
		pageAncestors = getAncestors(contentId, spaceKey);
	}

	const analyticsContext = useMemo(
		() => ({
			navVersion: 3,
			origin: 'advancedSearch',
			breadcrumbsTotal: pageAncestors.length + 1,
			searchResultIndex: index,
			searchResultId: contentId,
			searchResultSpaceKey: spaceKey,
			searchSessionId,
		}),
		[index, contentId, spaceKey, pageAncestors, searchSessionId],
	);

	const onExpand = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'expanded',
				actionSubject: 'searchBreadcrumbs',
				source: 'containerNavigation',
				attributes: {
					...analyticsContext,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, analyticsContext]);

	const onBreadcrumbItemClick = useCallback(
		(itemIndex: number, title: string) => () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'breadcrumbsItem',
					source: 'containerNavigation',
					attributes: {
						...analyticsContext,
						ancestorIndex: itemIndex,
						destination: itemIndex === 0 ? 'SPACE' : 'ANCESTOR',
						titleLength: title.length,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, analyticsContext],
	);

	const onBreadcrumbItemTooltipShown = useCallback(
		(itemIndex: number, title: string) => () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'displayed',
					actionSubject: 'searchBreadcrumbsTooltip',
					source: 'containerNavigation',
					attributes: {
						...analyticsContext,
						ancestorIndex: itemIndex,
						destination: itemIndex === 0 ? 'SPACE' : 'ANCESTOR',
						titleLength: title.length,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, analyticsContext],
	);

	const addSearchSessionToURL = useCallback(
		(href: any) => {
			return searchSessionId
				? addQueryParams(href, {
						search_id: searchSessionId,
					})
				: href;
		},
		[searchSessionId],
	);

	return (
		<SearchBreadcrumbsWrapper data-testid="search-breadcrumbs-wrapper">
			<AkBreadcrumbs maxItems={3} onExpand={onExpand}>
				{pageAncestors.map(({ href, id, title }, itemIndex) => (
					<BreadcrumbsItem
						key={id}
						truncationWidth={itemIndex === pageAncestors.length - 1 || itemIndex === 0 ? 192 : 50}
						testId={id}
						text={title}
						href={addSearchSessionToURL(href)}
						onClick={onBreadcrumbItemClick(itemIndex, title)}
						onTooltipShown={onBreadcrumbItemTooltipShown(itemIndex, title)}
					/>
				))}
			</AkBreadcrumbs>
		</SearchBreadcrumbsWrapper>
	);
};
